"use client"

import { createFileRoute } from '@tanstack/react-router'
import { useState, FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProjectByShareId } from '@/src/api/project'
import { ProjectView } from '@/src/components/manage/projects/ProjectView'
import { Spinner } from '@/src/components/ui/spinner'
import { Input } from '@/src/components/ui/input'
import { Button } from '@/src/components/ui/button'
import { useTranslation } from '@/src/i18n/client'

export const Route = createFileRoute('/project/$shareId')({
  component: ProjectSharePage,
})

function ProjectSharePage() {
  const { t } = useTranslation('share')
  const { shareId } = Route.useParams()
  const [password, setPassword] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['project', 'byShareId', shareId, password],
    queryFn: () => getProjectByShareId({ data: { shareId, password } }),
    enabled: submitted,
    retry: false,
  })

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    refetch()
  }

  if (!submitted || (!data && !error)) {
    return (
      <main className="flex flex-col items-center mt-12 sm:mt-24 px-4">
        <div className="text-6xl font-bold text-center mb-4">
          #{shareId}
        </div>

        <div className="flex flex-col items-center justify-center mt-8">
          <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 w-full max-w-xs">
            <Input
              type="password"
              placeholder={t('projectShare.enterPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="text-center"
              disabled={isLoading}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !password}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner size={12} />
                </span>
              ) : (
                t('projectShare.viewProject')
              )}
            </Button>
          </form>
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className="flex flex-col items-center mt-12 sm:mt-24 px-4">
        <div className="text-6xl font-bold text-center mb-4">
          #{shareId}
        </div>
        <p className="text-red-500 text-center mt-8">
          {error instanceof Error ? error.message : t('projectShare.errorMessage')}
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => {
            setSubmitted(false)
            setPassword('')
          }}
        >
          {t('projectShare.tryAgain')}
        </Button>
      </main>
    )
  }

  if (!data) {
    return null
  }

  return (
    <main className="container mx-auto px-0 sm:px-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{data.name}</h1>
        <div className="text-sm text-muted-foreground">{t('projectShare.shareIdLabel')} {shareId}</div>
      </div>
      <ProjectView project={data} isLoading={isLoading} />
    </main>
  )
}
